import { DragEvent } from 'react';

import { StarFilled } from '@ant-design/icons';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

import { AssignedPerson, OperationTurno } from 'models/OperationRequest';
import { Store } from 'models/Store';

import { AssignmentSlots, Blocker, peopleOf } from '../data/assignment';
import { storeLocation } from '../data/supervision';
import AssignedPersonChip from './AssignedPersonChip';

interface AssignmentListViewProps {
  turnos: OperationTurno[];
  storesById: Map<number, Store>;
  slots: AssignmentSlots;
  dragging: boolean;
  draggingFromKey: string | null;
  blockerFor: (turno: OperationTurno) => Blocker | null;
  dayLabel: (day: string) => string;
  onDrop: (turno: OperationTurno) => void;
  onPick: (turno: OperationTurno) => void;
  onChipDragStart: (
    person: AssignedPerson,
    fromKey: string,
    e: DragEvent<HTMLSpanElement>
  ) => void;
  onDragEnd: () => void;
  onOpenPerson: (leadId: string) => void;
  onRemove: (key: string, leadId: string) => void;
}

function AssignmentListView({
  turnos,
  storesById,
  slots,
  dragging,
  draggingFromKey,
  blockerFor,
  dayLabel,
  onDrop,
  onPick,
  onChipDragStart,
  onDragEnd,
  onOpenPerson,
  onRemove,
}: AssignmentListViewProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.board.list';

  return (
    <div className="assignment-board__list">
      <table>
        <thead>
          <tr>
            <th style={{ width: 74 }}>{t(`${i18nBase}.turno`)}</th>
            <th>{t(`${i18nBase}.store`)}</th>
            <th>{t(`${i18nBase}.day_block`)}</th>
            <th style={{ width: 90, textAlign: 'center' }}>
              {t(`${i18nBase}.cupos`)}
            </th>
            <th>{t(`${i18nBase}.assigned`)}</th>
            <th style={{ width: 120 }} />
          </tr>
        </thead>
        <tbody>
          {turnos.map((turno, i) => {
            const store = storesById.get(turno.storeId);
            const people = peopleOf(slots, turno.key);
            const full = people.length >= turno.cupos;
            const blocker = dragging ? blockerFor(turno) : null;
            const canDrop =
              dragging &&
              draggingFromKey !== turno.key &&
              (!blocker || !!blocker.soft);
            let countClass = 'assignment-board__turno-count';
            if (full) countClass += ' assignment-board__turno-count--full';
            else if (people.length) {
              countClass += ' assignment-board__turno-count--partial';
            }
            return (
              <tr
                key={turno.key}
                className={
                  canDrop ? 'assignment-board__list-row--drop' : undefined
                }
                onDragOver={(e) => {
                  if (canDrop) e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (canDrop) onDrop(turno);
                }}
              >
                <td className="assignment-board__list-index">{`#${i + 1}`}</td>
                <td>
                  <div className="assignment-board__store-name">
                    {store?.isVip && (
                      <StarFilled className="assignment-board__star" />
                    )}
                    <span>{store?.name || `#${turno.storeId}`}</span>
                  </div>
                  {store && (
                    <div className="assignment-board__store-loc">
                      {`${store.chain} · ${storeLocation(store)}`}
                    </div>
                  )}
                </td>
                <td>
                  <div className="assignment-board__list-day">
                    <span className="assignment-board__block-tag">
                      {turno.block.toUpperCase()}
                    </span>
                    <span>{dayLabel(turno.day)}</span>
                  </div>
                  <div className="assignment-board__turno-time">
                    {`${turno.from}–${turno.to}`}
                  </div>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className={countClass}>
                    {`${people.length}/${turno.cupos}`}
                  </span>
                </td>
                <td>
                  <div className="assignment-board__list-people">
                    {people.map((person) => (
                      <AssignedPersonChip
                        key={person.leadId}
                        person={person}
                        onDragStart={(e) =>
                          onChipDragStart(person, turno.key, e)
                        }
                        onDragEnd={onDragEnd}
                        onOpen={onOpenPerson}
                        onRemove={() => onRemove(turno.key, person.leadId)}
                      />
                    ))}
                    {people.length === 0 && (
                      <span className="assignment-board__list-empty">
                        {t(`${i18nBase}.unassigned`)}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {!full && (
                    <Button size="small" onClick={() => onPick(turno)}>
                      {t(`${i18nBase}.assign`)}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AssignmentListView;
